import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { endorsements, policies, type OperationDetail } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { caminhoDoAssistente, codigoDeEndosso } from './endosso';

/**
 * Emissao da cotacao de endosso (P-END-1), no lugar do painel de emissao da apolice.
 *
 * A cotacao de endosso passa pela mesma esteira e chega a mesma emissao, mas nao cria
 * apolice nem comissao: a emissao aplica o endosso na apolice endossada (o neutro troca
 * a fotografia das partes com a origem endosso) e move a operacao para emitida.
 */
export function EndorsementIssuancePanel({ operacao }: { operacao: OperationDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const naEtapa = operacao.stage === 'em_emissao';
  const jaEmitida = operacao.stage === 'emitida';

  const endosso = useQuery({
    queryKey: ['endorsements', { operacao: operacao.id }],
    queryFn: ({ signal }) =>
      endorsements.list({ operation_id: operacao.id, limit: 1 }, signal),
    enabled: can('endossos.endosso.view'),
    retry: false,
  });
  const ligado = endosso.data?.items[0];

  const emitir = useMutation({
    mutationFn: () => policies.issue(operacao.id, {}),
    onSuccess: () => {
      toast.success('Endosso emitido e aplicado na apolice.');
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void queryClient.invalidateQueries({ queryKey: ['endorsements'] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
    onError: (error: Error) => toast.error(error.message || 'Erro ao emitir o endosso'),
  });

  if (!naEtapa && !jaEmitida) return null;
  const codigo = codigoDeEndosso(ligado?.endorsement_type?.code);

  return (
    <Card>
      <CardHeader
        title="Emissao do endosso"
        description="A cotacao de endosso nao cria apolice: a emissao aplica o endosso na apolice endossada."
        action={
          naEtapa && can('emissao.apolice.create') ? (
            <Button loading={emitir.isPending} onClick={() => emitir.mutate()}>
              Emitir endosso
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-col gap-2 text-sm">
        {ligado ? (
          <div className="flex flex-wrap items-center gap-2">
            <span>Endosso {ligado.number}</span>
            <StatusBadge kind="situacaoEndosso" value={ligado.status} />
            {ligado.policy ? (
              <Link
                to={`/apolices/${ligado.policy.id}`}
                className="text-brand-600 underline-offset-2 hover:underline"
              >
                Apolice {ligado.policy.external_number || String(ligado.policy.number)}
              </Link>
            ) : null}
            {codigo && ligado.endorsement_subtype_id ? (
              <Link
                to={caminhoDoAssistente(
                  ligado.policy_id,
                  codigo,
                  ligado.endorsement_subtype_id,
                  ligado.id,
                )}
                className="text-brand-600 underline-offset-2 hover:underline"
              >
                Abrir o endosso
              </Link>
            ) : null}
          </div>
        ) : null}
        {endosso.isSuccess && !ligado ? (
          <Alert tone="warning" title="Cotacao de endosso sem endosso ligado">
            O endosso nasce do assistente de endosso da apolice. Esta cotacao nao veio
            de la, e a emissao vai ser recusada.
          </Alert>
        ) : null}
        {jaEmitida ? (
          <p className="text-ink-600">Endosso emitido e aplicado na apolice.</p>
        ) : null}
      </CardBody>
    </Card>
  );
}
